import { useIsFetching } from "@tanstack/react-query";
import type { Report } from "../lib/api/index.js";
import type { Membership } from "../lib/chosen-workspace.js";
import { useCanIn } from "../lib/queries/index.js";
import { PATHS, SCREEN_NAMES } from "../lib/routes.js";
import { usePathname, withReport } from "../lib/url-filters.js";
import { AccountMenu } from "./account-menu.js";
import {
  Blocks,
  LayoutDashboard,
  Library,
  MessageSquareText,
  Quote,
  Send,
  Settings,
  Split,
  Swords,
  Target,
  Telescope,
  Users,
} from "./icons.js";
import { BrandSwitch } from "./side/brand-switch.js";
import { at, SideNav, type SideRow } from "./side/side-nav.js";
import "./side/side.css";
import { WorkspaceSwitch } from "./side/workspace-switch.js";
import { WhatsNewCard } from "./whats-new/whats-new-card.js";
import { Wordmark } from "./wordmark.js";

/**
 * The application's navigation, down the left edge (DESIGN.md §5).
 *
 * Widest scope first: the workspace, then one brand in a box of its own with
 * only that brand's views inside, then what belongs to the whole workspace.
 * Everything inside the box is about the brand at its head, and nothing
 * outside it is — so choosing another brand changes only the box.
 *
 * It was a tree of brand folders until the second revamp (2026-10-12), then
 * one card at the top holding the brand and the workspace together, which
 * made the research tools and People read as the brand's (2026-10-13).
 *
 * Every screen the app can reach is listed. A nav that hides where you are is
 * how people conclude a feature does not exist.
 */

export function AppSidebar({
  workspaceId,
  workspaceName,
  workspaces,
  onChooseWorkspace,
  userName,
  email,
  reports,
  reportId,
  onSignOut,
  onTakeTour,
}: {
  readonly workspaceId: string;
  readonly workspaceName: string;
  readonly workspaces: readonly Membership[];
  readonly onChooseWorkspace: (workspaceId: string) => void;
  /** What the account is called, over its email at the foot of the rail. */
  readonly userName: string;
  readonly email: string;
  /** Every measured report in this workspace, in the workspace's order. */
  readonly reports: readonly Report[];
  /** Which one the screens are showing. Chosen in `App`, never here. */
  readonly reportId: string | null;
  readonly onSignOut: () => void;
  /** Starts the tour again, from the account menu. */
  readonly onTakeTour: () => void;
}) {
  const path = usePathname();
  const canAddBrands = useCanIn(workspaceId, "addBrands");
  // A page's data loading for the first time — not a background refresh, which
  // would set the mark moving every time capture progress polled.
  // Capture progress is left out: the rail reads it for every report, and the
  // mark is about the page, not about a pill beside a brand.
  const loading =
    useIsFetching({
      predicate: (query) =>
        query.state.data === undefined && query.queryKey[0] !== "capture-progress",
    }) > 0;

  const current = reports.find((report) => report.id === reportId);
  const role = workspaces.find((one) => one.workspaceId === workspaceId)?.role;
  const manages = role === "owner" || role === "admin";

  return (
    <aside className="side" aria-label="Sections" data-tour="rail">
      <div className="side-top">
        <Wordmark className="side-wordmark" introTarget busy={loading} />
      </div>

      <WorkspaceSwitch
        workspaceId={workspaceId}
        workspaceName={workspaceName}
        workspaces={workspaces}
        reportCount={reports.length}
        onChooseWorkspace={onChooseWorkspace}
      />

      <div className="side-brand">
        <BrandSwitch
          reports={reports}
          current={current}
          path={path}
          canAddBrands={canAddBrands}
          workspaceId={workspaceId}
        />
        {current === undefined ? null : (
          <SideNav
            label={null}
            name={current.brand.name}
            path={path}
            rows={brandRows(current)}
          />
        )}
      </div>

      <div className="side-scroll">
        <SideNav
          label="Across all brands"
          name="Across all brands"
          path={path}
          rows={[
            {
              href: PATHS.research,
              label: SCREEN_NAMES.research,
              icon: Telescope,
              isHere: at(PATHS.research, PATHS.urlPrompts),
            },
            {
              href: PATHS.fanout,
              label: SCREEN_NAMES.fanout,
              icon: Split,
              isHere: at(PATHS.fanout),
            },
            {
              href: PATHS.library,
              label: SCREEN_NAMES.library,
              icon: Library,
              isHere: at(PATHS.library),
            },
          ]}
        />
        {/* People is an owner's or an admin's, as its screen is; AI apps is
            everyone's (2026-10-14): each person connects their own. */}
        <SideNav
          label="Workspace"
          name="Workspace"
          path={path}
          rows={[
            ...(manages
              ? [
                  {
                    href: PATHS.members,
                    label: SCREEN_NAMES.members,
                    icon: Users,
                    isHere: at(PATHS.members),
                  },
                ]
              : []),
            {
              href: PATHS.aiApps,
              label: SCREEN_NAMES.aiApps,
              icon: Blocks,
              isHere: at(PATHS.aiApps),
            },
          ]}
        />
      </div>

      {/* What shipped lately, then who is signed in and the way out. */}
      <div className="side-foot">
        <WhatsNewCard />
        <AccountMenu
          name={userName}
          email={email}
          onSignOut={onSignOut}
          onTakeTour={onTakeTour}
        />
      </div>
    </aside>
  );
}

/** The brand's own views and its settings, each carrying the brand in its link. */
function brandRows(report: Report): readonly SideRow[] {
  return [
    {
      href: withReport(PATHS.overview, report.id),
      label: SCREEN_NAMES.overview,
      icon: LayoutDashboard,
      isHere: (path) => path === PATHS.overview || path === PATHS.home,
    },
    {
      href: withReport(PATHS.opportunities, report.id),
      label: SCREEN_NAMES.opportunities,
      icon: Target,
      isHere: at(PATHS.opportunities),
    },
    {
      href: withReport(PATHS.prompts, report.id),
      label: SCREEN_NAMES.prompts,
      icon: MessageSquareText,
      isHere: at(PATHS.prompts),
    },
    {
      href: withReport(PATHS.rivals, report.id),
      label: SCREEN_NAMES.rivals,
      icon: Swords,
      isHere: at(PATHS.rivals),
    },
    {
      href: withReport(PATHS.citations, report.id),
      label: SCREEN_NAMES.citations,
      icon: Quote,
      isHere: at(PATHS.citations),
    },
    {
      href: withReport(PATHS.offPage, report.id),
      label: SCREEN_NAMES.offPage,
      icon: Send,
      isHere: at(PATHS.offPage),
    },
    {
      href: withReport(PATHS.settings, report.id),
      label: SCREEN_NAMES.settings,
      icon: Settings,
      isHere: at(PATHS.settings),
    },
  ];
}
